import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { loginBody, TRUSTED_DEVICE_DAYS, type LoginBody, type LoginResult } from '@pulsepoint/shared';
import { z } from 'zod';

/** On the dev server the password may be blank, for the API's DEV_PASSWORDLESS_LOGINS. */
const devLogin = loginBody.extend({ password: z.string().max(128) });
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useLogin, useLoginCode, useMe } from '../../api/auth';
import { ApiError } from '../../api/client';
import { safeNext } from '../../auth/RequireAuth';
import { FormTextField } from '../../components/form/FormTextField';
import { PasswordField } from '../../components/form/PasswordField';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { qrWithLogo } from '../../lib/qr';
import { colours, radius } from '../../theme';

type CodeStep = Exclude<LoginResult, { mfa?: undefined }>;

/**
 * Sign in: the password, then a 6-digit code from an authenticator app. The first time, the
 * app is set up with a QR code. "Remember this device" skips the code here for 90 days.
 */
export function LoginPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const navigate = useNavigate();
  const { data: me } = useMe();
  const login = useLogin();
  const [error, setError] = useState('');
  const [step, setStep] = useState<CodeStep | null>(null);
  const form = useForm<LoginBody>({ resolver: zodResolver(import.meta.env.DEV ? devLogin : loginBody), defaultValues: { login: '', password: '' } });

  if (me) return <Navigate to={me.mustChangePassword ? '/change-password' : next} replace />;

  const submit = form.handleSubmit(async (values) => {
    setError('');
    try {
      const r = await login.mutateAsync(values);
      if (r.mfa) {
        setStep(r);
        return;
      }
      navigate(r.mustChangePassword ? '/change-password' : next, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.');
      form.setValue('password', '');
      form.setFocus('password');
    }
  });

  if (step) {
    return (
      <CodeForm
        step={step}
        onBack={() => {
          setStep(null);
          form.setValue('password', '');
        }}
        onDone={(mustChange) => navigate(mustChange ? '/change-password' : next, { replace: true })}
      />
    );
  }

  return (
    <AuthLayout title="Sign in." lead="Manage your chargers, drivers and sessions.">
      <Stack component="form" onSubmit={submit} noValidate spacing={2.5}>
        <FormTextField control={form.control} name="login" label="Username or email" autoComplete="username" autoFocus />
        <PasswordField control={form.control} name="password" label="Password" autoComplete="current-password" />
        {error && <Alert severity="error" role="alert">{error}</Alert>}
        <Button type="submit" variant="contained" size="large" disabled={login.isPending} sx={{ minHeight: 48 }}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </Button>
        <Link component={RouterLink} to="/forgot-password" sx={{ alignSelf: 'flex-start' }}>
          Forgot your password?
        </Link>
      </Stack>
    </AuthLayout>
  );
}

/** The code from the app; the first time, setting the app up with a QR code. */
function CodeForm({ step, onBack, onDone }: { step: CodeStep; onBack(): void; onDone(mustChangePassword: boolean): void }) {
  const send = useLoginCode();
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [qr, setQr] = useState('');
  const setup = step.mfa === 'setup' ? step : null;

  useEffect(() => {
    if (!setup) return;
    void qrWithLogo(setup.otpauthUrl).then(setQr);
  }, [setup]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const me = await send.mutateAsync({ ticket: step.ticket, code, remember });
      onDone(me.mustChangePassword);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onBack();
        return;
      }
      setError(err instanceof ApiError ? (err.fields.code ?? err.message) : 'Something went wrong. Please try again.');
      setCode('');
    }
  };

  return (
    <AuthLayout
      title={setup ? 'Set up two-factor sign-in.' : 'Enter your code.'}
      lead={setup ? 'Pulsepoint asks for a code from an authenticator app as well as your password. It takes a minute, once.' : 'Open your authenticator app and enter the 6-digit code for Pulsepoint.'}
    >
      <Stack component="form" onSubmit={submit} noValidate spacing={2.5}>
        {setup && (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Typography>
              1. Open an authenticator app on your phone, such as Google Authenticator, Microsoft Authenticator or 1Password, and add an account.
            </Typography>
            <Typography>2. Scan this QR code.</Typography>
            <Box
              role="img"
              aria-label="QR code for your authenticator app"
              sx={{ width: 200, height: 200, p: 1.5, border: `1px solid ${colours.hairline}`, borderRadius: `${radius.panel}px`, '& svg': { width: '100%', height: '100%' } }}
              dangerouslySetInnerHTML={{ __html: qr }}
            />
            <Typography variant="body2" color="textSecondary">
              Can't scan it? Enter this key in the app instead:{' '}
              <Box component="span" className="num" sx={{ fontWeight: 600, color: colours.nearBlack, letterSpacing: '0.06em', wordBreak: 'break-all' }}>
                {setup.secret.replace(/(.{4})/g, '$1 ').trim()}
              </Box>
            </Typography>
            <Typography>3. Enter the 6-digit code it shows.</Typography>
          </Box>
        )}
        <TextField
          label="6-digit code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^\d\s]/g, '').slice(0, 7))}
          autoFocus
          autoComplete="one-time-code"
          error={!!error}
          helperText={error || ' '}
          slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9 ]*', 'aria-label': '6-digit code', style: { letterSpacing: '0.3em', fontSize: 22 } } }}
        />
        <FormControlLabel control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} />} label={`Remember this device for ${TRUSTED_DEVICE_DAYS} days`} />
        <Button type="submit" variant="contained" size="large" disabled={send.isPending || code.replace(/\s/g, '').length !== 6} sx={{ minHeight: 48 }}>
          {send.isPending ? 'Checking…' : setup ? 'Confirm and sign in' : 'Sign in'}
        </Button>
        <Typography variant="body2" color="textSecondary">
          {setup ? 'Lost your phone later? An Infinity EV admin can reset this for you.' : 'Lost your phone? Ask an Infinity EV admin to reset your two-factor sign-in.'}{' '}
          <Link component="button" type="button" onClick={onBack}>Back</Link>
        </Typography>
      </Stack>
    </AuthLayout>
  );
}
